import React, { useEffect, useMemo } from 'react';
import { useActiveBlockTab } from '../../components/blocks/BlockAccessors';
import { ActivityOrderContent } from './ActivityOrderContent';
import { ActivityMessages } from './ActivityMessages';
import { ActivitySummary } from './ActivitySummary';
import { accountIdSelector, subAccountIdSelector, useStateSelector } from '../../data/global/dataSelectors';
import { areOrdersLoaded } from '../../data/global/selectors/orderSelector';
import { Loading, EmptyBlockContent } from '@tradingblock/components';
import { useBlockId } from '../../components/blocks/BlockState';
import { useActivityTimeframeValues } from './data/useActivityTimeframe';
import { useActivityBlockData, useActivitySettingsValue } from './useActivitySettings';
import { useDispatch } from 'react-redux';
import { Actions } from '../../data/global/actions';
import { AssetType, SpreadType } from '@tradingblock/types';
import { useQuoteActions } from '../../hooks/useQuoteData';

export type Spread = { id: number | undefined, name: string; type: SpreadType; Legs: { Symbol: string; AssetType: AssetType }[] };

export const ActivityContent: React.FunctionComponent<{}> = () => {
  const activeTab = useActiveBlockTab();
  const accountId = useStateSelector(accountIdSelector);
  const subaccountId = useStateSelector(subAccountIdSelector);
  const blockId = useBlockId();
  const { workingOrdersOnly } = useActivitySettingsValue();
  const { startDate, endDate } = useActivityTimeframeValues();
  const { pageSize, pageIndex } = useActivityBlockData();
  const dispatch = useDispatch();
  const tab = useMemo(() => (activeTab ? activeTab.title : undefined), [activeTab ? activeTab.title : undefined]);
  const isTradesTab = tab === 'Trades';
  const isMessagesTab = tab === 'Messages';
  const isSummaryTab = tab === 'Summary';
  const { setQuotes, setSpreads } = useQuoteActions();

  useEffect(() => {
    // Fetch when request inputs change (tab, timeframe, paging, account). Do not depend on loaded orders —
    // receiveAccountOrders updates orders state and would retrigger this effect in a loop.
    // Order updates are refreshed via OrderFeedMiddleware (debounced requestAccountOrders).
    // Short debounce coalesces rapid dependency changes; debounce keys include payload so this does not cancel other GetOrders callers.
    if (accountId && !isSummaryTab) {
      dispatch(
        Actions.requestAccountOrders(
          {
            accountId,
            subaccountId,
            dateFrom: startDate,
            dateTo: endDate,
            includeOrderEvents: isMessagesTab ? true : false,
            pageSize,
            pageIndex,
            blockId,
            isTradesTab,
            workingOrdersOnly: workingOrdersOnly,
          },
          { debounce: 200 }
        )
      );
    }
    if (isMessagesTab) {
      setQuotes([]);
      setSpreads([]);
    }
  }, [
    workingOrdersOnly,
    startDate,
    endDate,
    pageSize,
    pageIndex,
    accountId,
    subaccountId,
    isTradesTab,
    isMessagesTab,
    isSummaryTab,
    setQuotes,
    setSpreads,
    dispatch,
    blockId,
  ]);

  const isLoaded = useStateSelector(areOrdersLoaded);
  return (
    <>
      {isLoaded ? (
        <>
          {tab === 'Messages' && <ActivityMessages />}
          {tab === 'Orders' && <ActivityOrderContent tabType="orders" />}
          {tab === 'Trades' && <ActivityOrderContent tabType="trades" />}
          {tab === 'Summary' && <ActivitySummary />}
        </>
      ) : (
        <EmptyBlockContent>{<Loading />}</EmptyBlockContent>
      )}
    </>
  );
};
